import { useEffect, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapView } from "../components/MapView";
import { RouteEditorMap } from "../components/RouteEditorMap";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Zap } from "lucide-react";

const ESTADO_LABEL = { planificada: "Planificada", en_curso: "En curso", completada: "Completada" };

function AutoRouteDialog({ open, onClose, sectores, encargados, onGenerated }) {
  const [sectorId, setSectorId] = useState("");
  const [encargadoId, setEncargadoId] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [generando, setGenerando] = useState(false);

  useEffect(() => {
    if (open) {
      setSectorId("");
      setEncargadoId("");
      setFecha(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const generar = async () => {
    if (!sectorId) {
      toast.error("Selecciona un sector");
      return;
    }
    setGenerando(true);
    try {
      const { data } = await api.post("/routes/generar-automatica", {
        sector_id: sectorId,
        encargado_id: encargadoId || null,
        fecha_planificada: fecha,
      });
      const metodo = data.metodo_ruteo === "arcgis_calles" ? "por calles reales (ArcGIS)" : "por línea recta (heurística)";
      toast.success(`Ruta generada con ${data.stops.length} paradas óptimas — ${metodo}`);
      onGenerated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la ruta automática");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="auto-route-dialog">
        <DialogHeader>
          <DialogTitle>Generar ruta automática</DialogTitle>
          <DialogDescription>
            Las incidencias pendientes del sector se ordenarán por cercanía GPS (vecino más cercano) y se asignarán al encargado más próximo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Sector</Label>
            <Select value={sectorId} onValueChange={setSectorId}>
              <SelectTrigger data-testid="auto-route-sector-select">
                <SelectValue placeholder="Selecciona un sector" />
              </SelectTrigger>
              <SelectContent>
                {sectores.map((s) => (
                  <SelectItem key={s._id} value={s._id}>
                    {s.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Encargado (opcional, se elige el más cercano si se deja vacío)</Label>
            <Select value={encargadoId} onValueChange={setEncargadoId}>
              <SelectTrigger data-testid="auto-route-encargado-select">
                <SelectValue placeholder="Automático (el más cercano)" />
              </SelectTrigger>
              <SelectContent>
                {encargados.map((e) => (
                  <SelectItem key={e._id} value={e._id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Fecha planificada</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="auto-route-fecha-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={generar} disabled={generando} data-testid="generate-auto-route-button" className="bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
            {generando ? "Generando..." : "Generar ruta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RouteEditorDialog({ open, onClose, initial, sectores, encargados, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [sectorId, setSectorId] = useState("");
  const [encargadoId, setEncargadoId] = useState("");
  const [fecha, setFecha] = useState("");
  const [stops, setStops] = useState([]);

  useEffect(() => {
    if (open) {
      setNombre(initial?.nombre || "");
      setSectorId(initial?.sector_id || "");
      setEncargadoId(initial?.encargado_id || "");
      setFecha(initial?.fecha_planificada || new Date().toISOString().slice(0, 10));
      setStops(initial?.stops || []);
    }
  }, [open, initial]);

  const addStop = (lat, lon) => {
    setStops((s) => [
      ...s,
      { id: `stop-${Date.now()}`, orden: s.length + 1, nombre: `Punto de control ${s.length + 1}`, lat, lon, ventana_inicio: "08:00", ventana_fin: "14:00", visitado: false, checkin: null },
    ]);
  };

  const removeStop = (id) => setStops((s) => s.filter((x) => x.id !== id));

  const guardar = async () => {
    if (!nombre || stops.length === 0) {
      toast.error("Indica un nombre y al menos un punto de control sobre el mapa");
      return;
    }
    const payload = { nombre, sector_id: sectorId || null, encargado_id: encargadoId || null, fecha_planificada: fecha, stops };
    try {
      if (initial?._id) {
        await api.patch(`/routes/${initial._id}`, payload);
        toast.success("Ruta actualizada");
      } else {
        await api.post("/routes", payload);
        toast.success("Ruta creada");
      }
      onSaved();
      onClose();
    } catch {
      toast.error("No se pudo guardar la ruta");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="route-editor-dialog">
        <DialogHeader>
          <DialogTitle>{initial?._id ? "Editar ruta de calidad" : "Nueva ruta de calidad"}</DialogTitle>
          <DialogDescription>Define los datos de la ruta y haz clic en el mapa para añadir puntos de control.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Nombre de la ruta</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="route-nombre-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Sector</Label>
              <Select value={sectorId} onValueChange={setSectorId}>
                <SelectTrigger data-testid="route-sector-select">
                  <SelectValue placeholder="Sin sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectores.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Encargado</Label>
              <Select value={encargadoId} onValueChange={setEncargadoId}>
                <SelectTrigger data-testid="route-encargado-select">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  {encargados.map((e) => (
                    <SelectItem key={e._id} value={e._id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Fecha planificada</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="route-fecha-input" />
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Haz clic en el mapa para añadir puntos de control</p>
            <RouteEditorMap stops={stops} onAddStop={addStop} height="280px" />
          </div>

          <div className="space-y-1.5" data-testid="route-editor-stops-list">
            {stops.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border-b border-border py-1.5 last:border-0">
                <span>{s.nombre}</span>
                <button onClick={() => removeStop(s.id)} data-testid={`remove-stop-${s.id}`}>
                  <X className="h-3.5 w-3.5 text-muted-foreground hover:text-accent-alert" />
                </button>
              </div>
            ))}
            {stops.length === 0 && <p className="text-xs text-muted-foreground">Aún no hay puntos de control.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} data-testid="save-route-button" className="bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
            Guardar ruta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function QualityRoutes() {
  const [routes, setRoutes] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sectores, setSectores] = useState([]);
  const [encargados, setEncargados] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);

  const load = () => {
    api.get("/routes").then((r) => {
      setRoutes(r.data);
      setSelected((prev) => r.data.find((x) => x._id === prev?._id) || r.data[0] || null);
    });
    api.get("/routes/compliance/resumen").then((r) => setCompliance(r.data));
  };

  useEffect(() => {
    load();
    api.get("/gis/sectores").then((r) => setSectores(r.data));
    api.get("/incidents/encargados-disponibles").then((r) => setEncargados(r.data)).catch(() => {});
  }, []);

  const eliminar = async (id) => {
    await api.delete(`/routes/${id}`);
    toast.success("Ruta eliminada");
    load();
  };

  const incidentesComoPuntos = (selected?.stops || []).map((s) => ({
    _id: s.id,
    lat: s.lat,
    lon: s.lon,
    estado: s.visitado ? "Verificada" : "Nueva",
    codigo: s.nombre,
  }));

  return (
    <AppLayout title="Rutas de control de calidad">
      <div className="flex items-center justify-between mb-6">
        <div className="grid grid-cols-2 gap-4 flex-1 max-w-md">
          <Card>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Cumplimiento global</p>
              <p className="text-3xl font-black text-valoriza-leaf mt-2" data-testid="compliance-global-pct">
                {compliance?.pct_cumplimiento_global ?? "—"}%
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Puntos visitados</p>
              <p className="text-3xl font-black mt-2">
                {compliance?.puntos_visitados ?? "—"} / {compliance?.total_puntos ?? "—"}
              </p>
            </CardContent>
          </Card>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="auto-route-button" variant="outline" onClick={() => setAutoOpen(true)} className="gap-2">
            <Zap className="h-4 w-4" /> Generar automática
          </Button>
          <Button
            data-testid="new-route-button"
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
            className="gap-2 bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90"
          >
            <Plus className="h-4 w-4" /> Nueva ruta
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest">Rutas planificadas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="quality-routes-list">
            {routes.map((r) => (
              <div
                key={r._id}
                onClick={() => setSelected(r)}
                data-testid={`route-item-${r._id}`}
                className={`w-full text-left rounded-md px-3 py-3 border cursor-pointer transition-colors ${selected?._id === r._id ? "border-valoriza-leaf bg-valoriza-leaf/10" : "border-border hover:bg-secondary"}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold">{r.nombre}</p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(r);
                        setEditorOpen(true);
                      }}
                      data-testid={`edit-route-${r._id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        eliminar(r._id);
                      }}
                      data-testid={`delete-route-${r._id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-accent-alert" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <Badge variant="outline" className="text-[10px]">{ESTADO_LABEL[r.estado]}</Badge>
                  <span className="text-xs text-muted-foreground">{r.stops.filter((s) => s.visitado).length}/{r.stops.length} puntos</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <MapView incidents={incidentesComoPuntos} routeGeometry={selected?.ruta_geometria || null} showHeatmap={false} height="420px" />
          {selected && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-sm uppercase tracking-widest">Puntos de control — {selected.nombre}</CardTitle>
                {selected.metodo_ruteo && (
                  <Badge variant="outline" className="text-[10px]" data-testid="route-metodo-badge">
                    {selected.metodo_ruteo === "arcgis_calles" ? "Ruta por calles reales (ArcGIS)" : "Ruta en línea recta (heurística)"}
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {selected.stops.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b border-border py-2 last:border-0 text-sm">
                    <span>{s.nombre}</span>
                    {s.visitado ? (
                      <span className="text-valoriza-leaf font-bold text-xs">✓ Valoración {s.checkin?.valoracion}/5</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Pendiente</span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <RouteEditorDialog open={editorOpen} onClose={() => setEditorOpen(false)} initial={editing} sectores={sectores} encargados={encargados} onSaved={load} />
      <AutoRouteDialog open={autoOpen} onClose={() => setAutoOpen(false)} sectores={sectores} encargados={encargados} onGenerated={load} />
    </AppLayout>
  );
}
